// Shared loader for a project's variables, so the page designer's pickers
// (block bindings, embed args, embed filters) can offer them for selection.
//
// At runtime the page runtime seeds every project variable into the page-vars
// store (see RuntimeContext: `vars[name]` = value, `vars[name+'__label']` =
// label), so an author can bind/filter/pass-as-arg to a project variable by
// name without an `api` input. This hook surfaces the same names in the
// authoring dropdowns.
//
// Results are cached per project (module-level) so the many picker instances
// in the property panel don't each refetch.

import { useEffect, useState } from 'react'

import { listProjectVariables, type ProjectVariable } from '@/api/projectVariables'

export interface ProjectVarOption {
  value: string   // the variable ref to store as the binding/arg/filter name
  label: string   // dropdown display text
}

const cache = new Map<string, Promise<ProjectVariable[]>>()

function load(project: string): Promise<ProjectVariable[]> {
  let p = cache.get(project)
  if (!p) {
    p = listProjectVariables(project).catch(() => [] as ProjectVariable[])
    cache.set(project, p)
  }
  return p
}

/** Invalidate the cache for a project (call after editing its variables). */
export function invalidateProjectVariables(project: string): void {
  cache.delete(project)
}

function toOptions(vars: ProjectVariable[]): ProjectVarOption[] {
  const out: ProjectVarOption[] = []
  for (const v of vars) {
    if (!v?.name) continue
    out.push({ value: v.name, label: `${v.name} (value)` })
    // Prompt (select) variables also expose a human-readable label var.
    if (v.kind === 'prompt') {
      out.push({ value: `${v.name}__label`, label: `${v.name} (label)` })
    }
  }
  return out
}

/**
 * Returns the project's variables as selectable `{ value, label }` options
 * (value refs + a `__label` ref for prompt variables). Empty until loaded /
 * when `project` is falsy.
 */
export function useProjectVariables(project: string | undefined): ProjectVarOption[] {
  const [opts, setOpts] = useState<ProjectVarOption[]>([])
  useEffect(() => {
    if (!project) { setOpts([]); return }
    let cancelled = false
    load(project).then((vars) => { if (!cancelled) setOpts(toOptions(vars)) })
    return () => { cancelled = true }
  }, [project])
  return opts
}
